import React, { useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'

import { addToQuerystring } from '../../../utils/querystring'
import { getColorForIndex } from '../../../utils/colors'

import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceArea,
    Legend,
} from 'recharts'

import moment from 'moment'

const CustomTooltip = ({ active, payload, label }) => {
    const hasValue = tagValue => tagValue.value !== 0
    const byValueDesc = (tagValueA, tagValueB) =>
        tagValueB.value - tagValueA.value

    if (active && payload) {
        const total = payload.reduce((agg, curr) => agg + curr.value, 0)

        return (
            <div className="custom-tooltip">
                <h4>{moment(label).format('DD MMM HH:mm')}</h4>
                <div>
                    <span
                        className="m-r-sm"
                        style={{
                            width: '30px',
                            display: 'inline-block',
                        }}
                    >
                        <strong>{total}</strong>
                    </span>
                    <span>
                        <strong>Total</strong>
                    </span>
                </div>
                {payload
                    .filter(hasValue)
                    .sort(byValueDesc)
                    .map(tagValue => (
                        <div key={tagValue.name}>
                            <span
                                className="m-r-sm"
                                style={{
                                    width: '30px',
                                    display: 'inline-block',
                                }}
                            >
                                <strong>{tagValue.value}</strong>
                            </span>
                            <span
                                style={{
                                    color: tagValue.stroke,
                                }}
                            >
                                <strong>{tagValue.name}</strong>
                            </span>
                        </div>
                    ))}
            </div>
        )
    }

    return null
}

const GetScansByTagValueChart = ({ data }) => {
    const [refAreaLeft, setAreaLeft] = useState('')
    const [refAreaRight, setAreaRight] = useState('')
    const location = useLocation()
    const history = useHistory()

    const updateRoute = (startDate, endDate) => {
        const newLocation = addToQuerystring(location, {
            startDate: startDate && moment(startDate).format('YYYYMMDDTHHmm'),
            endDate: endDate && moment(endDate).format('YYYYMMDDTHHmm'),
        })

        history.push(newLocation)
    }

    const zoom = () => {
        if (refAreaLeft === refAreaRight || refAreaRight === '') {
            setAreaLeft('')
            setAreaRight('')
            return
        }

        const leftArea = moment(refAreaLeft)
        const rightArea = moment(refAreaRight)

        if (leftArea.isSameOrBefore(rightArea)) {
            updateRoute(refAreaLeft, refAreaRight)
            return
        }
        updateRoute(refAreaRight, refAreaLeft)
    }

    const getDaysBetweenFirstAndLastScan = data => {
        if (data.scans.length < 2) return -1

        const firstScan = moment(data.scans[0].QuarterHour)
        const lastScan = moment(data.scans[data.scans.length - 1].QuarterHour)

        return lastScan.diff(firstScan, 'days')
    }

    const onlyShowTimeOnXAxis = getDaysBetweenFirstAndLastScan(data) === 0

    // reverse the default order of the legend
    const legend = (data.tagValues || [])
        .map((tagValue, index) => ({
            dataKey: tagValue.name,
            type: 'line',
            color: getColorForIndex(index),
            value: tagValue.name,
            total: tagValue.numberOfScans,
        }))
        .sort((tagValueA, tagValueB) => tagValueB.total - tagValueA.total)

    return (
        <ResponsiveContainer width="100%" height={250}>
            <AreaChart
                data={data.scans || []}
                margin={{
                    top: 10,
                    right: 30,
                    left: 0,
                    bottom: 0,
                }}
                onMouseDown={e => {
                    if (e && e.activeLabel) {
                        setAreaLeft(e.activeLabel)
                    }
                }}
                onMouseMove={e => {
                    if (refAreaLeft && e && e.activeLabel) {
                        setAreaRight(e.activeLabel)
                    }
                }}
                onMouseUp={zoom}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    padding={{ left: 20, right: 20 }}
                    dataKey="QuarterHour"
                    tickFormatter={timeStr =>
                        onlyShowTimeOnXAxis
                            ? moment(timeStr).format('HH:mm')
                            : moment(timeStr).format('DD MMM HH:mm')
                    }
                />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Legend payload={legend} />
                {(data.tagValues || []).map((tagValue, index) => (
                    <Area
                        key={tagValue.name}
                        type="monotone"
                        activeDot={{ r: 3 }}
                        stackId="a"
                        dataKey={tagValue.name}
                        strokeWidth={2}
                        fill={getColorForIndex(index)}
                        fillOpacity="0.2"
                        stroke={getColorForIndex(index)}
                    />
                ))}

                {refAreaLeft && refAreaRight ? (
                    <ReferenceArea
                        x1={refAreaLeft}
                        x2={refAreaRight}
                        strokeOpacity={0.3}
                    />
                ) : null}
            </AreaChart>
        </ResponsiveContainer>
    )
}
export default GetScansByTagValueChart
